import { useState } from 'react';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { usePurchaseTileBulkActionsState } from 'src/components/Purchases/BulkActions/PurchasesTileBulkActions/usePurchaseTileBulkActionsState';
import { useBulkAdjustCost } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkAdjustCost';
import { useBulkCancelPurchase } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkCancelPurchase';
import { useBulkChangeEventAction } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkChangeEventAction';
import { useBulkDeleteTicketGroup } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkDeleteTicketGroup';
import { useBulkEditCommissionAction } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkEditCommissionAction';
import { useBulkUpdateDeliveryType } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdateDeliveryType';
import { useBulkUpdateInternalNotesAction } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdateInternalNotesAction';
import { useBulkUpdatePurchaseCurrencyCode } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdatePurchaseCurrencyCodeAction';
import { useBulkUpdatePurchaseSettings } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdatePurchaseGeneralSettings';
import { useBulkUpdateSeatTraits } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdateSeatTraits';
import { useExportPurchasesAction } from 'src/components/Purchases/PurchaseActionDropdown/components/LaunchExportPurchasesAction/useExportPurchasesAction';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ChevronDownIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

import { PurchasesBulkActionProps } from '../../PurchasesGlobalBulkActions/PurchasesBulkActions.types';
import { useUpdatePurchasesPermissions } from '../../useUpdatePurchasesPermissions';

type SettingsActionsProps = {
  groupId: string;
};

export const SettingsActions = ({
  groupId,
  isEventView,
}: SettingsActionsProps & PurchasesBulkActionProps) => {
  const { areBulkActionsEnabled } = useBulkActionsState();
  const [isLoading, setIsLoading] = useState(false);

  const { filterQueryWithEventIds } = usePurchaseTileBulkActionsState(
    groupId,
    isEventView
  );

  const bulkUpdateGeneralSettings = useBulkUpdatePurchaseSettings(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    groupId,
    !isEventView
  );

  const bulkUpdateDeliveryType = useBulkUpdateDeliveryType(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    groupId,
    isEventView
  );

  const bulkUpdateSeatTraits = useBulkUpdateSeatTraits(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    groupId,
    isEventView
  );

  const bulkUpdateInternalNotes = useBulkUpdateInternalNotesAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    groupId,
    !isEventView
  );

  const bulkEditCommissionAction = useBulkEditCommissionAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    groupId,
    !isEventView
  );

  const bulkCancelPurchaseAction = useBulkCancelPurchase(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    groupId,
    !isEventView
  );

  const bulkDeleteTicketGroupAction = useBulkDeleteTicketGroup(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    groupId,
    isEventView
  );

  const bulkChangeEvent = useBulkChangeEventAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    groupId,
    isEventView
  );

  const bulkUpdatePurchaseCurrencyCode = useBulkUpdatePurchaseCurrencyCode(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    groupId,
    !isEventView
  );

  const bulkAdjustCost = useBulkAdjustCost(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const exportPurchasesAction = useExportPurchasesAction({
    style: { color: vars.color.textStrong, width: '100%' },
    variant: 'text',
    shape: 'rect',
    filterQuery: filterQueryWithEventIds,
    disabled: !areBulkActionsEnabled,
    textContentId: ContentId.ExportPurchases,
    textOnly: true,
    isEventView: isEventView ?? false,
  });

  const {
    hasUpdatePurchasesPermission,
    hasPurchaseUpdateCommissionPermission,
  } = useUpdatePurchasesPermissions();

  if (!hasUpdatePurchasesPermission && !hasPurchaseUpdateCommissionPermission) {
    return null;
  }

  return (
    <>
      <PosDropdown
        key="purchases-settings-actions-dropdown"
        trigger={
          <Button variant="outline">
            <Content id={ContentId.Settings} />
            <ChevronDownIcon
              size={vars.iconSize.xs}
              fill={IconsFill.currentColor}
            />
          </Button>
        }
        align="start"
        triggerProps={{ disabled: isLoading || !areBulkActionsEnabled }}
      >
        {!isEventView && bulkUpdateGeneralSettings.dropDown}

        {bulkUpdateDeliveryType.dropDown}
        {bulkUpdateSeatTraits.dropDown}

        {!isEventView && bulkUpdateInternalNotes.dropDown}
        {!isEventView && bulkEditCommissionAction.dropDown}
        {!isEventView && bulkCancelPurchaseAction.dropDown}

        {isEventView && bulkDeleteTicketGroupAction.dropDown}
        {isEventView && bulkChangeEvent.dropDown}

        {exportPurchasesAction.dropdown}
        {!isEventView && bulkUpdatePurchaseCurrencyCode.dropDown}
        {bulkAdjustCost.dropDown}
      </PosDropdown>
      {isEventView && bulkChangeEvent.dialog}
    </>
  );
};
